import React, { createContext, useState, useEffect, useCallback, useMemo, PropsWithChildren } from 'react';
import Cookies from 'universal-cookie';

import { current } from '../services/api/users';
import { AuthToken } from '../entities/auth-token';
import { Administrator } from '../entities/administrator';

const COOKIE_NAME_TOKEN = 'admin_token';
const COOKIE_DOMAIN = process.env.REACT_APP_COOKIE_DOMAIN;
const cookies = new Cookies();

interface ContextValue {
  token: string;
  loading: boolean;
  user?: Administrator;
  setUserToken: (authToken: AuthToken) => void;
  signout: () => void;
}

const AuthContext = createContext<ContextValue>({
  token: '',
  loading: true,
  setUserToken: () => null,
  signout: () => null,
});

function AuthContextProvider({ children }: PropsWithChildren<Record<string, unknown>>) {
  const [token, setToken] = useState<string>(cookies.get(COOKIE_NAME_TOKEN));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<Administrator>();

  const signout = useCallback(() => {
    setToken('');
    setUser(undefined);
    cookies.remove(COOKIE_NAME_TOKEN, { domain: COOKIE_DOMAIN, path: '/' });
  }, [setToken]);

  const setUserToken = useCallback(
    (authToken: AuthToken) => {
      cookies.set(COOKIE_NAME_TOKEN, authToken.token, {
        expires: authToken.expiredAt,
        domain: COOKIE_DOMAIN || undefined,
        path: '/',
      });

      setToken(authToken.token);
      setIsLoading(true);
    },
    [setToken],
  );

  useEffect(() => {
    const fetchUser = async (currToken: string) => {
      try {
        const currUser = await current(currToken);

        setUser(currUser);
        setIsLoading(false);
      } catch (e) {
        setUser(undefined);
        setIsLoading(false);
      }
    };

    if (!user && token) {
      fetchUser(token);
    } else {
      setIsLoading(false);
    }
  }, [token, setUserToken, user]);

  const contextValue = useMemo(
    () => ({
      token,
      signout,
      user,
      setUserToken,
      loading: isLoading,
    }),
    [token, user, signout, setUserToken, isLoading],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthContextProvider };
